import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Layers,
  AlertTriangle,
  HardDrive,
  Scan,
  FolderTree,
  Rocket,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  deploymentsApi,
  servicesApi,
  verifiedStateApi,
  groupsApi,
} from "@/api/client";
import { formatRelativeTime, titleCase } from "@/lib/utils";
import DeploymentDetailsModal from "@/components/DeploymentDetailsModal";

export default function Dashboard() {
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);

  // Fetch recent completed deployments (success/completed/failed)
  const { data: recentDeployments } = useQuery({
    queryKey: ["deployments", "recent", { limit: 5 }],
    queryFn: async () => {
      // Fetch completed deployments - the API doesn't support multiple statuses,
      // so we filter client-side for now
      const result = await deploymentsApi.list({ limit: 20 });
      const completedStatuses = ["success", "completed", "failed"];
      const filtered = (result.data ?? []).filter((d) =>
        completedStatuses.includes(d.status),
      );
      return { ...result, data: filtered.slice(0, 5) };
    },
  });

  // Fetch upcoming deployments (draft, pending_approval, approved, deploying)
  const { data: upcomingDeployments } = useQuery({
    queryKey: ["deployments", "upcoming", { limit: 5 }],
    queryFn: async () => {
      const result = await deploymentsApi.list({ limit: 20 });
      const upcomingStatuses = [
        "draft",
        "pending_approval",
        "approved",
        "deploying",
      ];
      const filtered = (result.data ?? []).filter((d) =>
        upcomingStatuses.includes(d.status),
      );
      return { ...result, data: filtered.slice(0, 5) };
    },
  });

  const { data: services } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const { data: dashboardStats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => verifiedStateApi.getDashboardStats(),
  });

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ["groups-dashboard"],
    queryFn: () => groupsApi.listWithDashboardStats(),
  });

  const stats = [
    {
      name: "Groups",
      value: dashboardStats?.total_groups ?? (groupsData?.groups ?? []).length,
      icon: FolderTree,
      color: "text-purple-600 bg-purple-100",
      link: "/groups",
    },
    {
      name: "Servers",
      value: dashboardStats?.total_servers ?? 0,
      icon: HardDrive,
      color: "text-indigo-600 bg-indigo-100",
      link: "/servers",
    },
    {
      name: "Services",
      value: (services?.services ?? []).length,
      icon: Layers,
      color: "text-green-600 bg-green-100",
      link: "/services",
    },
    {
      name: "Drift Detected",
      value: dashboardStats?.unresolved_drift ?? 0,
      icon: AlertTriangle,
      color:
        dashboardStats?.unresolved_drift && dashboardStats.unresolved_drift > 0
          ? "text-red-600 bg-red-100"
          : "text-green-600 bg-green-100",
      link: "/drift-events",
    },
    {
      name: "Total Deployments",
      value:
        dashboardStats?.total_deployments ??
        recentDeployments?.pagination?.total ??
        0,
      icon: Rocket,
      color: "text-blue-600 bg-blue-100",
      link: "/deployments",
    },
    {
      name: "Recent Scans",
      value: dashboardStats?.recent_scans ?? 0,
      icon: Scan,
      color: "text-cyan-600 bg-cyan-100",
      link: "/scans",
    },
  ];

  return (
    <div>
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your deployment operations
        </p>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((stat) => {
          const content = (
            <div className="flex items-center">
              <div className={`rounded-md p-3 ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          );

          return stat.link ? (
            <Link
              key={stat.name}
              to={stat.link}
              className="card hover:shadow-md transition-shadow"
            >
              {content}
            </Link>
          ) : (
            <div key={stat.name} className="card">
              {content}
            </div>
          );
        })}
      </div>

      {/* Groups Overview */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              Groups Overview
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Deployment groups with drift detection status
            </p>
          </div>
          <Link
            to="/groups"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View all groups →
          </Link>
        </div>

        {groupsLoading ? (
          <div className="mt-4 text-gray-500">Loading...</div>
        ) : (
          <div className="mt-4 grid gap-6 lg:grid-cols-2">
            {(groupsData?.groups ?? []).map((group) => (
              <Link
                key={group.id}
                to={`/groups/${group.slug}`}
                className={`card hover:shadow-md transition-shadow ${group.has_drift ? "border-l-4 border-l-red-500" : ""}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">{group.name}</h3>
                  <div className="flex items-center gap-2">
                    {group.has_drift ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                        <AlertTriangle className="h-3 w-3" />
                        {group.drift_count} drift
                        {group.drift_count !== 1 ? "s" : ""}
                      </span>
                    ) : group.server_count > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        In sync
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                        Not scanned
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Environments:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {group.environment_count}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Services:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {group.service_count}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Servers:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {group.server_count}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Components:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {group.component_count}
                    </span>
                  </div>
                </div>

                {group.last_activity_at && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    Last activity: {formatRelativeTime(group.last_activity_at)}
                  </div>
                )}
              </Link>
            ))}

            {(!groupsData?.groups || groupsData.groups.length === 0) && (
              <div className="card col-span-2 text-center py-8">
                <FolderTree className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No groups
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a group.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upcoming Deployments */}
      {(upcomingDeployments?.data ?? []).length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Upcoming Deployments
            </h2>
            <Link
              to="/deployments"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View all deployments →
            </Link>
          </div>

          <div className="mt-4 card overflow-hidden p-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Environment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(upcomingDeployments?.data ?? []).map((deployment) => (
                  <tr
                    key={deployment.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedDeploymentId(deployment.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {titleCase(deployment.service_name || "")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-mono">
                      {deployment.version}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {deployment.environment_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <StatusBadge status={deployment.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(deployment as any).title || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(deployment.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recently Completed Deployments */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            Recently Completed Deployments
          </h2>
          <Link
            to="/deployments"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View all deployments →
          </Link>
        </div>

        <div className="mt-4 card overflow-hidden p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Deployed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  When
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(recentDeployments?.data ?? []).map((deployment) => (
                <tr
                  key={deployment.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedDeploymentId(deployment.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <button
                      className="hover:text-blue-600 text-left"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDeploymentId(deployment.id);
                      }}
                    >
                      {titleCase(deployment.service_name || "")}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                    <button
                      className="hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDeploymentId(deployment.id);
                      }}
                    >
                      {deployment.version}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.environment_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.group_name ? (
                      <Link
                        to={`/groups/${deployment.group_slug}`}
                        className="hover:text-blue-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deployment.group_name}
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.deployed_by || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatRelativeTime(deployment.deployed_at)}
                  </td>
                </tr>
              ))}

              {(recentDeployments?.data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No deployments recorded yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deployment Details Modal */}
      {selectedDeploymentId && (
        <DeploymentDetailsModal
          deploymentId={selectedDeploymentId}
          onClose={() => setSelectedDeploymentId(null)}
        />
      )}
    </div>
  );
}

// Status badge component for deployment status
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<
    string,
    { bg: string; text: string; icon: React.ReactNode; label: string }
  > = {
    success: {
      bg: "bg-green-100",
      text: "text-green-800",
      icon: <CheckCircle className="h-3 w-3 mr-1" />,
      label: "Success",
    },
    completed: {
      bg: "bg-green-100",
      text: "text-green-800",
      icon: <CheckCircle className="h-3 w-3 mr-1" />,
      label: "Completed",
    },
    failed: {
      bg: "bg-red-100",
      text: "text-red-800",
      icon: <XCircle className="h-3 w-3 mr-1" />,
      label: "Failed",
    },
    draft: {
      bg: "bg-gray-100",
      text: "text-gray-800",
      icon: <FileText className="h-3 w-3 mr-1" />,
      label: "Draft",
    },
    pending_approval: {
      bg: "bg-yellow-100",
      text: "text-yellow-800",
      icon: <Clock className="h-3 w-3 mr-1" />,
      label: "Pending Approval",
    },
    approved: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      icon: <CheckCircle className="h-3 w-3 mr-1" />,
      label: "Approved",
    },
    deploying: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      icon: <Rocket className="h-3 w-3 mr-1" />,
      label: "Deploying",
    },
    cancelled: {
      bg: "bg-gray-100",
      text: "text-gray-800",
      icon: <XCircle className="h-3 w-3 mr-1" />,
      label: "Cancelled",
    },
  };

  const config = statusConfig[status] || {
    bg: "bg-gray-100",
    text: "text-gray-800",
    icon: null,
    label: status,
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}
